import {
  COMBINABLE_EXTENSIONS,
  DOCUMENT_CATEGORIES,
  DOCUMENT_TYPES,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
} from './constants';
import {
  canParseFile,
  getFileExtension,
  resolveParseWorkflow,
  type ParseWorkflowType,
} from './parseWorkflow';
import type { DocumentInfoValues, StagedUpload, UploadedDocument } from './types';
import {
  getUnparsedAlert,
  getUploadErrorAlert,
  getUploadParseNotice,
  listRejections,
  trimFileName,
  uploadFailureReason,
  type UploadRejection,
} from './utils';

/**
 * Pure helpers for a batch of uploads (MCV-1765). The Documents view machine keeps one batch in
 * its context and sends the jobs to the single-flight documents service one at a time.
 */

/**
 * One POST. A single file is its own document; 2+ files are combined into one packet PDF.
 * `docType` is the File Type the job uploads with: the file's own (MCV-1765), or 'packet' for a
 * combined job. Undefined for a drop, which has no form (the service falls back to 'other').
 * [MCV-1848] `docCategory` is the File Category the file was given on its row ('' for none, and
 * always '' for a combined job); undefined takes the form's shared category, as a drop has none.
 */
export type UploadJob = { files: File[]; docType?: string; docCategory?: string };

export type AlertText = { text: string; subText: string };

export type UploadOutcome =
  | {
      ok: true;
      name: string;
      // The parse workflow started for it, or the type parses but this format does not.
      parses: boolean;
      storedOnly: boolean;
      // The workflow its type and category lead to (null: the type never parses).
      workflow: ParseWorkflowType | null;
      notice: string | undefined;
      document: UploadedDocument;
    }
  | { ok: false; name: string; reason: string; alert: AlertText };

export interface UploadBatch {
  providerId: number;
  inFlight: UploadJob;
  pending: UploadJob[];
  // The number of jobs, i.e. documents the batch creates.
  total: number;
  // The form values every document shares (each job overrides the File Type); null for a drop.
  values: DocumentInfoValues | null;
  source: 'modal' | 'drop';
  outcomes: UploadOutcome[];
  // Dropped files turned away before any upload, for the summary.
  rejected: UploadRejection[];
}

export type UploadSummary = {
  text: string;
  subText?: string;
  type: 'success' | 'warning' | 'error';
  timeout: number;
  // Stays until the user closes it (MCV-1800).
  persistent?: boolean;
};

/** The name as a PDF's: a combinable extension (.png, .jpg, ...) becomes .pdf, else .pdf is added. */
export const withPdfExtension = (name: string): string => {
  const ext = getFileExtension(name).toLowerCase();
  const stem = (COMBINABLE_EXTENSIONS as readonly string[]).includes(ext)
    ? name.slice(0, -(ext.length + 1))
    : name;
  return `${stem}.pdf`;
};

/**
 * Why these files cannot be combined into one packet, or null when they can. The API merges
 * PDFs and images only (it has no Word-to-PDF converter), and the merged PDF is one upload.
 */
export const getCombineBlocker = (files: File[]): string | null => {
  const combinable: readonly string[] = COMBINABLE_EXTENSIONS;
  if (files.some((file) => !combinable.includes(getFileExtension(file.name).toLowerCase()))) {
    const labels = COMBINABLE_EXTENSIONS.map((ext) => ext.toUpperCase());
    return `Only ${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]} files can be combined.`;
  }
  if (files.reduce((total, file) => total + file.size, 0) > MAX_FILE_SIZE_BYTES) {
    return `Files to combine must total ${MAX_FILE_SIZE_MB}MB or less.`;
  }
  return null;
};

/** One job per file, or a single job holding every file when they are combined into one packet. */
export const buildUploadJobs = (
  files: (Pick<StagedUpload, 'file'> & Partial<StagedUpload>)[],
  combine: boolean
): UploadJob[] =>
  combine && files.length > 1
    ? [{ files: files.map(({ file }) => file), docType: 'packet', docCategory: '' }]
    : files.map(({ file, docType, docCategory }) => ({ files: [file], docType, docCategory }));

const combinedName = (values: DocumentInfoValues) => trimFileName(withPdfExtension(values.docName));

/**
 * The form values one job uploads with. A lone file keeps the name the user typed; with several
 * documents each is saved under its own file name; a combined packet is named as a PDF. The job's
 * File Type replaces the form's (MCV-1765: each file has its own), and so does its File Category
 * (MCV-1848), an empty one meaning none.
 */
export const metadataFor = (
  job: UploadJob,
  values: DocumentInfoValues | null,
  total: number
): DocumentInfoValues | undefined => {
  if (!values) return undefined;
  const withType = job.docType
    ? {
        ...values,
        docType: DOCUMENT_TYPES.find(({ value }) => value === job.docType) ?? {
          label: job.docType,
          value: job.docType,
        },
      }
    : values;
  const shared =
    job.docCategory === undefined
      ? withType
      : {
          ...withType,
          docCategory: DOCUMENT_CATEGORIES.find(({ value }) => value === job.docCategory) ?? {
            label: job.docCategory,
            value: job.docCategory,
          },
        };
  if (job.files.length > 1) return { ...shared, docName: combinedName(values) };
  if (total === 1) return { ...shared, docName: trimFileName(values.docName) };
  return { ...shared, docName: trimFileName(job.files[0].name) };
};

type UploadResultEvent =
  | { type: 'ON_DOCUMENT_UPLOADED'; documentReference: UploadedDocument }
  | { type: 'ON_DOCUMENT_UPLOAD_ERROR'; error: unknown };

/** What the service reported for the job in flight. */
export const toOutcome = (
  job: UploadJob,
  values: DocumentInfoValues | null,
  event: UploadResultEvent
): UploadOutcome => {
  const combined = job.files.length > 1;
  // What the API judges the parse by is the name of the file it was sent.
  const fileName = combined
    ? values
      ? combinedName(values)
      : 'packet.pdf'
    : trimFileName(job.files[0].name);
  if (event.type === 'ON_DOCUMENT_UPLOAD_ERROR') {
    return {
      ok: false,
      name: fileName,
      reason: uploadFailureReason(event.error),
      alert: getUploadErrorAlert(event.error, { combined }),
    };
  }
  const document = event.documentReference;
  const typeAndCategory = {
    docType: document.docType ?? '',
    docCategory: document.docCategory ?? '',
  };
  const workflow = resolveParseWorkflow(typeAndCategory);
  const parses = workflow !== null && canParseFile(workflow, fileName);
  return {
    ok: true,
    name: fileName,
    parses,
    storedOnly: workflow !== null && !parses,
    workflow,
    notice: getUploadParseNotice(typeAndCategory, fileName),
    document,
  };
};

type OkOutcome = Extract<UploadOutcome, { ok: true }>;
type FailedOutcome = Extract<UploadOutcome, { ok: false }>;

const sentence = (...parts: (string | undefined)[]) => parts.filter(Boolean).join(' ') || undefined;

/**
 * The toasts for the whole batch, in the order they are shown. First what happened to the
 * uploads; then, for a DROP, when some files were stored without parsing because their format is
 * not one their File Type parses, ONE persistent error naming them (MCV-1800).
 */
export const summarizeUploads = ({
  inFlight,
  outcomes,
  rejected,
  source,
  total,
}: Pick<
  UploadBatch,
  'inFlight' | 'outcomes' | 'rejected' | 'source' | 'total'
>): UploadSummary[] => {
  const succeeded = outcomes.flatMap((outcome): OkOutcome[] => (outcome.ok ? [outcome] : []));
  const failed = outcomes.flatMap((outcome): FailedOutcome[] => (outcome.ok ? [] : [outcome]));
  const rejectedPart =
    rejected.length > 0 ? `Not uploaded: ${listRejections(rejected)}.` : undefined;
  const unparsed = succeeded.flatMap(({ name, storedOnly, workflow }) =>
    storedOnly && workflow ? [{ name, workflow }] : []
  );
  // [MCV-1802] The Add Document list already marked each such file "Will be uploaded, not
  // parsed" before Save, so a modal batch does not repeat it in a toast. A drop has no list.
  const unparsedAlert: UploadSummary[] =
    source === 'drop' && unparsed.length > 0
      ? [{ ...getUnparsedAlert(unparsed), type: 'error', timeout: 0, persistent: true }]
      : [];

  if (total === 1) {
    const [only] = outcomes;
    if (!only.ok) {
      return [
        {
          text: only.alert.text,
          subText: sentence(only.alert.subText, rejectedPart),
          type: 'error',
          timeout: 6000,
        },
      ];
    }
    // A file stored without parsing is explained by the error that follows (a drop) or by the
    // note on its row of the Add Document list (the modal, MCV-1802).
    const subText = sentence(only.storedOnly ? undefined : only.notice, rejectedPart);
    return [
      {
        text:
          inFlight.files.length > 1
            ? `Combined ${inFlight.files.length} files into one packet`
            : 'Document upload complete',
        subText,
        type: 'success',
        timeout: subText ? 6000 : 4000,
      },
      ...unparsedAlert,
    ];
  }

  // Files that failed to upload and dropped files turned away before it are one list.
  const notUploaded = [
    ...failed.map(({ name, reason }) => `${name} (${reason})`),
    ...(rejected.length > 0 ? [listRejections(rejected)] : []),
  ].join(', ');
  const notUploadedPart = notUploaded ? `Not uploaded: ${notUploaded}.` : undefined;
  const parsed = succeeded.filter(({ parses }) => parses).length;
  const details = sentence(
    parsed > 0 ? `Parsing has started for ${parsed} of them.` : undefined,
    notUploadedPart
  );
  if (succeeded.length === 0) {
    return [{ text: 'No documents uploaded', subText: details, type: 'error', timeout: 8000 }];
  }
  if (failed.length > 0) {
    return [
      {
        text: `${succeeded.length} of ${total} documents uploaded`,
        subText: details,
        type: 'warning',
        timeout: 8000,
      },
      ...unparsedAlert,
    ];
  }
  return [
    {
      text: 'Document upload complete',
      subText: sentence(`${succeeded.length} documents uploaded.`, details),
      type: 'success',
      timeout: 6000,
    },
    ...unparsedAlert,
  ];
};
